import React, { useState, useEffect } from 'react';
import { useSession } from '~/components/contexts/SessionProvider';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronDownIcon, PlusIcon } from '@heroicons/react/24/outline';

export const AgentSelector = () => {
  const { owner } = useSession();
  const { agentId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation('navigation');
  const [isOpen, setIsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Detect if we're on mobile
  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  const sortedAgents = [...owner.agents].sort((a, b) => a.name.localeCompare(b.name));
  let currentAgent = sortedAgents.find((agent) => agent._id === agentId);

   const handleAgentSelect = (selectedAgentId: string) => {
    navigate(`/agent/${selectedAgentId}/dashboard`);
    setIsOpen(false);
  };

  const handleCreateAgent = () => {
    navigate('/create_agent');
    setIsOpen(false);
  };

  return (
    <div className="relative w-full">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`btn btn-ghost bg-base-200 rounded-lg btn-sm flex w-full items-center justify-between font-poppins shadow-sm ${
          isMobile 
            ? 'px-3 py-2 text-sm mt-2' 
            : 'px-2 py-1.5 text-xs'
        }`}
        title={currentAgent?.name || t('select_agent')}
      >
        <span className="flex-1 truncate text-left">
          {currentAgent?.name || t('select_agent')}
        </span>
        <ChevronDownIcon className={`ml-1 flex-shrink-0 ${isMobile ? 'h-4 w-4' : 'h-3 w-3'}`} />
      </button>

      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          />

          {/* Dropdown */}
          <div className={`absolute top-full z-20 mt-1 max-h-64 overflow-hidden rounded-box border border-base-300 bg-base-100 shadow-lg ${
            isMobile 
              ? 'left-0 right-0' 
              : 'left-0 w-64'
          }`}>
            <div className="max-h-60 overflow-y-auto py-1">
              {sortedAgents.map((agent) => (
                <button
                  key={agent._id}
                  onClick={() => handleAgentSelect(agent._id)}
                  className={`w-full truncate px-3 py-2 text-left text-sm font-poppins hover:bg-base-200 ${
                    agent._id === agentId
                      ? 'bg-primary/10 text-primary'
                      : 'text-base-content'
                  }`}
                  title={agent.name}
                >
                  {agent.name}
                </button>
              ))}

              <hr className="divider my-1" />

              <button
                onClick={handleCreateAgent}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-base-content hover:bg-base-200 font-poppins"
              >
                <PlusIcon className="h-4 w-4" />
                {t('create_agent')}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
